/**
 * @fileoverview PageWithSidebarNew component that provides a layout with a sidebar and main content area
 * @module PageWithSidebarNew
 */

import * as React from 'react';
import { Fragment, FunctionComponent } from 'react';
import { Flex } from 'basecamp';

/**
 * Props for the PageWithSidebarNew component
 * @typedef {Object} Props
 * @property {string} [dataTest] - Data test attribute for testing
 * @property {React.ReactNode[]} [headerElements] - Array of elements to render in the header
 * @property {number} [offsetHeight] - Height offset to adjust content area
 * @property {any} sidebarContent - Content to render in the sidebar
 */
type Props = {
	dataTest?: string;
	headerElements?: React.ReactNode[];
	offsetHeight?: number;
	sidebarContent: any;
};

/**
 * Component that renders a page layout with a sidebar and main content area
 * @param {Props} props - Component props
 * @param {string} [props.dataTest] - Data test attribute for testing
 * @param {React.ReactNode[]} [props.headerElements] - Array of elements to render in the header
 * @param {number} [props.offsetHeight=0] - Height offset to adjust content area
 * @param {any} props.sidebarContent - Content to render in the sidebar
 * @param {React.ReactNode} props.children - Content to render in main content area
 * @returns {JSX.Element} Rendered component
 */
const PageWithSidebarNew: FunctionComponent<Props> = ({
	dataTest,
	sidebarContent,
	headerElements,
	children,
	offsetHeight = 0,
}) => {
	/**
	 * Renders header elements if provided
	 * @returns {JSX.Element} Fragment containing header elements or empty Fragment
	 */
	const renderHeaderElements = () => {
		if (headerElements && headerElements.length > 0) {
			return headerElements.map((component, index) => <Fragment key={index}>{component}</Fragment>);
		}
		return <Fragment />;
	};

	/**
	 * Render the page layout
	 */
	return (
		<Fragment>
			{/* Header Elements Section */}
			<Flex id="pageHeaderElements" setAs="column" flexShrink={0} p={0} m={0} width={1}>
				{renderHeaderElements()}
			</Flex>

			{/* Main Content Area */}
			<Flex
				id="content"
				setAs="row"
				withAlign="distributeTop"
				dataTest={dataTest}
				flexGrow={1}
				zIndex={9}
				position="relative"
				px={4}
				py={0}
				m={0}
				pb={offsetHeight}
				overflowY="auto"
			>
				{/* Sidebar Section */}
				<Flex
					setAs="column"
					position="relative"
					zIndex={1040}
					width={['130px', '130px', '200px']}
					mr={4}
					dataTest={`${dataTest} sidebar`}
					height="fit-content"
				>
					{sidebarContent}
				</Flex>

				{/* Main Content Section */}
				<Flex
					setAs="column"
					position="relative"
					width={1}
					dataTest={`${dataTest} content`}
					height="fit-content"
				>
					{children}
				</Flex>
			</Flex>
		</Fragment>
	);
};

export default PageWithSidebarNew;
